import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const ProductCard = ({ product = {}, delay = '0s', brand = '', categoryId = '' }) => {
  const [imageError, setImageError] = useState(false);
  const navigate = useNavigate();

  // Safely extract properties with defaults
  const {
    image = '/assets/placeholder.jpg',
    name = 'Product',
    id = 'unknown',
    categoryName = ''
  } = product || {};

  // Add schema markup to head
  useEffect(() => {
    const schemaData = {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": name,
      "image": image,
      "brand": {
        "@type": "Brand",
        "name": brand || categoryName
      },
      "description": getProductDescription(name, brand),
      "url": `https://galaxypowersystems.com/product/${encodeURIComponent(id)}`,
      // Adding offers to fix Google structured data errors
      "offers": {
        "@type": "Offer",
        "url": `https://galaxypowersystems.com/product/${encodeURIComponent(id)}`,
        "priceCurrency": "INR",
        "price": getProductPrice(name, brand),
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": "Galaxy Power System",
          "url": "https://galaxypowersystems.com"
        }
      },
      // Adding aggregateRating as secondary option
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.5",
        "reviewCount": "100",
        "bestRating": "5",
        "worstRating": "1"
      }
    };

    // Create script tag with schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schemaData);
    script.id = `product-schema-${id}`;
    
    // Remove old schema if it exists
    const oldScript = document.getElementById(`product-schema-${id}`);
    if (oldScript) {
      oldScript.remove();
    }
    
    // Add new schema to head
    document.head.appendChild(script);

    return () => {
      const script = document.getElementById(`product-schema-${id}`);
      if (script) {
        script.remove();
      }
    };
  }, [name, brand, id, image]);

  const handleImageError = (e) => {
    console.log(`Image failed to load: ${image}`);
    setImageError(true);
    e.target.src = '/assets/placeholder.jpg';
    e.target.alt = 'Image not available';
  };

  const handleEyeClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Create product data object
    const productData = {
      id,
      name,
      image,
      brand,
      category: categoryName,
      categoryId,
      specifications: getProductSpecifications(name, brand),
      description: getProductDescription(name, brand),
      warranty: getWarrantyInfo(brand),
      price: getProductPrice(name, brand)
    };

    // Store in localStorage for single page
    localStorage.setItem('currentProduct', JSON.stringify(productData));
    
    // Navigate to single product page
    navigate(`/product/${encodeURIComponent(id)}`);
  };

  // Helper functions for product data
  const getProductPrice = (productName, brand) => {
    // Returns pricing based on brand and product type
    const prices = {
      exide: '4500',
      luminous: '5200',
      amaron: '5800',
      solar: '8500',
      lithium: '12000'
    };
    return prices[brand] || '4500';
  };

  const getProductSpecifications = (productName, brand) => {
    const specs = {
      exide: {
        'Voltage': '12V',
        'Technology': 'Advanced Lead Acid',
        'Maintenance': 'Low Maintenance',
        'Warranty': '36 months warranty'
      },
      luminous: {
        'Voltage': '12V',
        'Technology': 'SMF Technology',
        'Maintenance': 'Maintenance Free',
        'Warranty': '48 months warranty'
      },
      amaron: {
        'Voltage': '12V',
        'Technology': 'Advanced Technology',
        'Maintenance': 'Zero Maintenance',
        'Warranty': '60 months warranty'
      },
      solar: {
        'Voltage': '12V / 48V',
        'Technology': 'Solar Grade',
        'Maintenance': 'Low Maintenance',
        'Warranty': '24 months warranty'
      },
      lithium: {
        'Voltage': '12.8V / 48V',
        'Technology': 'LiFePO4 Lithium',
        'Maintenance': 'Zero Maintenance',
        'Warranty': '60 months warranty'
      }
    };
    
    // Get the base specifications for this brand
    const brandSpecs = { ...specs[brand] } || { ...specs.exide };
    
    // Extract capacity from product name if possible
    const capacityMatch = productName.match(/(\d+(\.\d+)?)\s*(Ah|AH|ah|W|KVA|kva)/i);
    if (capacityMatch && brandSpecs) {
      brandSpecs['Capacity'] = `${capacityMatch[1]} ${capacityMatch[3]}`;
    }
    
    return brandSpecs;
  };

  const getProductDescription = (productName, brand) => {
    const descriptions = {
      exide: `The ${productName} from Exide is engineered with cutting-edge technology to deliver superior performance and reliability. Built with advanced features for enhanced durability and longer service life, making it the perfect choice for your power needs.`,
      luminous: `Luminous ${productName} offers exceptional power backup with innovative design. Featuring advanced technology for optimal performance, this battery ensures reliable power supply with minimal maintenance requirements.`,
      amaron: `Amaron ${productName} is designed with cutting-edge technology for maximum efficiency and longevity. Known for its robust construction and reliable performance, it's the ideal choice for demanding applications.`,
      solar: `Our Solar ${productName} is engineered for optimal performance in renewable energy systems. Built with high-grade materials for maximum efficiency and durability in solar power applications.`,
      lithium: `The ${productName} features advanced LiFePO4 lithium technology for superior energy storage. With zero maintenance requirements, extended lifespan, and smart BMS systems, it's the future of power backup solutions. Perfect for modern homes and businesses requiring reliable, eco-friendly power.`
    };
    return descriptions[brand] || descriptions.exide;
  };

  const getWarrantyInfo = (brand) => {
    const warranties = {
      exide: '36 months warranty with 18 months free replacement',
      luminous: '48 months warranty with 24 months free replacement',
      amaron: '60 months warranty with 30 months free replacement',
      solar: '24 months warranty with 12 months free replacement',
      lithium: '60 months warranty with advanced support and zero maintenance cost'
    };
    return warranties[brand] || warranties.exide;
  };

  return (
    <div 
      className="product-card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        backgroundColor: '#fff',
        transition: 'transform 0.3s, box-shadow 0.3s',
        animation: `fadeInUp 0.6s ease-out forwards`,
        animationDelay: delay,
        opacity: 1,
        cursor: 'pointer',
        height: '100%'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-5px)';
        e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
      }}
    >
      {/* Image Container */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: '200px',
        backgroundColor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        <img 
          src={image} 
          alt={name}
          onError={handleImageError}
          loading="lazy"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            padding: '10px',
            display: 'block'
          }}
        />
        
        {/* Eye Icon Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0)',
          opacity: 0,
          transition: 'opacity 0.3s',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
        onMouseLeave={(e) => e.currentTarget.style.opacity = '0'}
        >
          <button 
            onClick={handleEyeClick}
            style={{
              color: '#fff',
              fontSize: '20px',
              backgroundColor: 'rgba(0,0,0,0.6)',
              width: '45px',
              height: '45px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textDecoration: 'none',
              transition: 'background-color 0.3s',
              border: 'none',
              cursor: 'pointer',
              outline: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(222, 64, 14, 0.9)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.6)'}
          >
            <i className="fa fa-eye"></i>
          </button>
        </div>
      </div>

      {/* Text Content */}
      <div style={{
        padding: '16px',
        textAlign: 'center',
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <span style={{
          display: 'block',
          fontSize: '16px',
          fontWeight: '600',
          color: '#333',
          textDecoration: 'none',
          lineHeight: '1.4'
        }}>
          {name}
        </span>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default ProductCard;